import { CASCADE_ENUM } from '../../../../services/enum.service';
import {
  HyperlinkStaticCellRenderer,
  IGridHyperlink,
} from '../../../../utils/custom-renderer/hyperlink-cell-renderer';
import {
  IAgGridRadio,
  RadioButtonCellRendered,
} from '../../../../utils/custom-renderer/radio-button-cell-renderer';
import { CONTRACT_TYPE, ICONS, ICON_COLORS } from '../../../../utils';
import {
  formatCodDate,
  getCreditProfileValue,
  getPriceValue,
  getStatusColour,
} from '../../../../utils/formatting';
import type { VppaListing } from './vppa-listing';
import { CUSTOM_HEADER_TOOLTIPS, headerWithTooltipColParams } from '../../../../utils/grid';

function getStatusStyle(params: any) {
  const response = {};

  if (params.data?.VPPA_STATUS) {
    response['color'] = getStatusColour(params.data?.VPPA_STATUS);
  }

  return response;
}

export enum COLUMN_FIELDS {
  TICKER = 'TICKER',
  UNIQUE_ID = 'UNIQUE_ID',
  TECHNOLOGY = 'TECHNOLOGY',
  PROJECT = 'NAME',
  ISO = 'ISO',
  SETTLEMENT_POINT = 'SETTLEMENT_POINT',
  VOLUME = 'OFFERED_CAPACITY',
  FIXED_PRICE = 'FIXED_PRICE',
  START_DATE = 'TENOR_START_DATE',
  TENOR_MONTHS = 'TENOR_MONTHS',
  COLLATERAL = 'SELLER_POST_COD_COLLATERAL',
  IMPLIED_CARBON_PRICE = 'IMPLIED_CARBON_PRICE',
  STATUS = 'VPPA_STATUS',
}

export function VPPA_LISTING_COLUMNS(context: VppaListing) {
  return [
    {
      maxWidth: 35,
      cellRenderer: RadioButtonCellRendered,
      cellRendererParams: (params) => {
        const agGridRadioParams: IAgGridRadio = {
          isSelected: false,
          project: params.data,
          callback: (detail) => {
            context.selectVppa(detail);
          },
        };
        if (context.selectedVppa && context.selectedVppa === params.data.VPPA_ID) {
          agGridRadioParams.isSelected = true;
        }
        return agGridRadioParams;
      },
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TICKER,
      headerName: 'Ticker',
      flex: 1,
      valueGetter: (params) => {
        const iconList: Array<{ icon: ICONS; width; height; fill? }> = [];

        if (params.data.CONTRACT_TYPE === CONTRACT_TYPE.MINI_POST_BUILD_VPPA_AUTO_EXECUTED) {
          iconList.push({
            icon: ICONS.AUTO_EXECUTED_ICON,
            width: '16px',
            height: '16px',
            fill: ICON_COLORS.AUTO_EXECUTED_STATUS,
          });
        }

        return JSON.stringify({
          NAME: params.data.TICKER,
          ICON_LIST: iconList,
        });
      },
      cellRenderer: HyperlinkStaticCellRenderer,
      cellRendererParams: (params) => {
        if (!params.value && !params.data) return;

        const obj = JSON.parse(params.value);
        const hyperlinkParams: IGridHyperlink = {
          value: obj?.NAME,
          id: '',
          vppaId: null,
          iconList: obj?.ICON_LIST,
        };

        return hyperlinkParams;
      },
    },
    {
      field: COLUMN_FIELDS.UNIQUE_ID,
      headerName: 'Unique Id',
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.PROJECT,
      headerName: 'Project',
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TECHNOLOGY,
      headerName: 'Technology',
      filter: 'agSetColumnFilter',
      filterValueGetter: (params) =>
        context.enumService.getEnumTranslation(CASCADE_ENUM.TECHNOLOGY, params.data.TECHNOLOGY),
      valueFormatter: (params) =>
        context.enumService.getEnumTranslation(CASCADE_ENUM.TECHNOLOGY, params.data.TECHNOLOGY),
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.ISO,
      headerName: 'ISO',
      filter: 'agSetColumnFilter',
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.SETTLEMENT_POINT,
      headerName: 'Settlement Point',
      filter: 'agSetColumnFilter',
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.VOLUME,
      headerName: 'Volume (MWH)',
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.FIXED_PRICE,
      headerName: 'Fixed Price ',
      valueFormatter: (params) => getPriceValue(params.data.FIXED_PRICE, '', '$'),
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.START_DATE,
      headerName: 'Start Date',
      valueFormatter: (params) => {
        return formatCodDate(params.data.TENOR_START_DATE);
      },
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.TENOR_MONTHS,
      headerName: 'Tenor (mo)',
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.COLLATERAL,
      headerName: 'Credit Profile ($/MW)',
      valueFormatter: (params) =>
        getCreditProfileValue(
          params.data.VPPA_CREDIT_PROFILE,
          params.data.SELLER_POST_COD_COLLATERAL,
        ),
      flex: 1,
      ...headerWithTooltipColParams(CUSTOM_HEADER_TOOLTIPS.CREDIT_PROFILE),
    },
    {
      field: COLUMN_FIELDS.IMPLIED_CARBON_PRICE,
      headerName: 'Implied Carbon Price',
      valueFormatter: (params) => getPriceValue(params.data.IMPLIED_CARBON_PRICE, '', '$'),
      flex: 1,
    },
    {
      field: COLUMN_FIELDS.STATUS,
      headerName: 'Status',
      filter: 'agSetColumnFilter',
      cellStyle: (params) => getStatusStyle(params),
      filterValueGetter: (params) =>
        context.enumService.getEnumTranslation(CASCADE_ENUM.VPPA_STATUS, params.data.VPPA_STATUS),
      valueFormatter: (params) =>
        context.enumService.getEnumTranslation(CASCADE_ENUM.VPPA_STATUS, params.data.VPPA_STATUS),
      flex: 1,
    },
  ];
}
